import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Input } from "@-/dev.improve.haniverse.components.ui.input";
import { Label } from "@-/dev.improve.haniverse.components.ui.label";
import {
  Surface,
  SurfaceContent,
  SurfaceDescription,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { View } from "@-/com.npmjs.react-native";
import * as React from "react";

export type TwoFactorChallengeFormProps = {
  /** Disables inputs and the primary button while a request is in flight. */
  busy?: boolean;
  /** Controlled code value; falls back to internal state when omitted. */
  code?: string;
  /** Cancel handler (e.g. navigate back to the sign-in form). */
  onCancel?: () => void;
  onChangeCode?: (value: string) => void;
  /** Submit handler. Receives the entered code. */
  onSubmit?: (values: { code: string }) => void;
  /** Toggle between TOTP and backup-code entry. */
  onToggleBackupCode?: () => void;
  /** When `true`, the form prompts for a one-time backup code instead of a TOTP code. */
  useBackupCode?: boolean;
};

/**
 * Presentational TOTP / backup-code challenge shown during sign-in when the
 * account has two-factor auth enabled. The wiring (calling
 * `twoFactor.verifyTotp` / `verifyBackupCode`) lives in `BlockAuthForm`; this
 * block only owns the input + submit affordances, mirroring `VerifyEmailForm`.
 */
export function TwoFactorChallengeForm({
  busy = false,
  code: codeProp,
  onCancel,
  onChangeCode,
  onSubmit,
  onToggleBackupCode,
  useBackupCode = false,
}: TwoFactorChallengeFormProps = {}) {
  const [codeState, setCodeState] = React.useState("");
  const code = codeProp ?? codeState;

  function handleChangeCode(value: string) {
    if (onChangeCode != null) {
      onChangeCode(value);
    } else {
      setCodeState(value);
    }
  }

  function handleSubmit() {
    onSubmit?.({ code });
  }

  return (
    <View className="gap-6">
      <Surface className="border-border/0 pb-4 shadow-none sm:border-border sm:shadow-sm sm:shadow-black/5">
        <SurfaceHeader>
          <SurfaceTitle className="text-center text-xl sm:text-left">
            Two-factor authentication
          </SurfaceTitle>
          <SurfaceDescription className="text-center sm:text-left">
            {useBackupCode
              ? "Enter one of your saved backup codes to continue."
              : "Enter the 6-digit code from your authenticator app to continue."}
          </SurfaceDescription>
        </SurfaceHeader>
        <SurfaceContent className="gap-6">
          <View className="gap-6">
            <View className="gap-1.5">
              <Label htmlFor="two-factor-code">
                {useBackupCode ? "Backup code" : "Authentication code"}
              </Label>
              <Input
                // Distinct field label so automation focuses the INPUT directly.
                accessibilityLabel="Two-factor code"
                autoCapitalize="none"
                editable={!busy}
                id="two-factor-code"
                keyboardType={useBackupCode ? "default" : "numeric"}
                onChangeText={handleChangeCode}
                onSubmitEditing={handleSubmit}
                returnKeyType="send"
                textContentType="oneTimeCode"
                value={code}
              />
            </View>
            <Button
              accessibilityLabel="Submit two-factor code"
              className="w-full"
              disabled={busy}
              onPress={handleSubmit}
            >
              <Text>Verify</Text>
            </Button>
            <Button
              disabled={busy}
              onPress={() => {
                onToggleBackupCode?.();
              }}
              size="sm"
              variant="link"
            >
              <Text className="text-center text-sm">
                {useBackupCode ? "Use authenticator app instead" : "Use a backup code instead"}
              </Text>
            </Button>
            <Button className="mx-auto" disabled={busy} onPress={onCancel} variant="ghost">
              <Text>Cancel</Text>
            </Button>
          </View>
        </SurfaceContent>
      </Surface>
    </View>
  );
}
